import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import ActionsAdDiscard, {actionsAdDiscardTypes} from '@adv-mt/mt-actions-ad-discard'
import ActionAdFavorite, {
  actionAdFavoriteTrackingOrigins,
  actionAdFavoriteTypes,
  actionAdUnfavoriteTrackingOrigins
} from '@adv-mt/mt-actions-ad-favorite'
import ActionsAdShare, {
  actionAdShareButtonSizes,
  actionAdShareTrackingOrigins,
  actionAdShareTypes
} from '@adv-mt/mt-actions-ad-share'
import {FavoriteLeadPopover} from '@adv-mt/mt-navigation-ad-toolbar'
import IconShareOutline from '@adv-mt/mt-svg-icons/lib/ShareOutline'
import {TooltipProvider} from '@adv-mt/ui/tooltip'

import ActionTooltip from '../ActionTooltip.js'
import {AD_ACTIONS_VARIANTS, BASE_CLASS} from './config.js'
import getDefaultBackUrl from './getDefaultBackUrl.js'

const AdActions = ({
  ad,
  breadcrumb = [],
  contractId,
  extendedTrackingData,
  hasDiscardButton,
  hrefBack,
  isFavorite,
  isFavoriteLeadEnabled,
  isNegative,
  onFavoriteLeadConfirm,
  variant = AD_ACTIONS_VARIANTS.DETAIL
}) => {
  const {domain, i18n} = useContext(Context)
  const router = useRouter()
  const config = domain.get('config')

  // Mirrors legacy's NavigationAdToolbar: the popover opens only on the "added" edge of the
  // toggle, driven by ActionAdFavorite's own onClick(isAddingFavorite) callback below.
  const [isFavoriteLeadPopoverOpen, setIsFavoriteLeadPopoverOpen] = useState(false)
  const handleFavoriteAction = isAddingFavorite => setIsFavoriteLeadPopoverOpen(isAddingFavorite)
  const handleFavoriteLeadPopoverClose = () => setIsFavoriteLeadPopoverOpen(false)

  const {HIT_ORIGINS} = config.get('tracking')
  const companyUrl = config.get('COMPANY_URL')
  const adsListPathPattern = config.get('page').SECOND_HAND_PATH_PATTERN

  const ORIGINS_BY_VARIANT = {
    [AD_ACTIONS_VARIANTS.DETAIL]: {
      discard: HIT_ORIGINS.AD_DETAIL_AD_DISCARDED,
      favorite: actionAdFavoriteTrackingOrigins.DETAIL_TOPBAR_AD_FAVORITED,
      unfavorite: actionAdUnfavoriteTrackingOrigins.DETAIL_TOPBAR_AD_UNFAVORITED,
      share: actionAdShareTrackingOrigins.DETAIL_TOPBAR_AD_SHARED
    },
    [AD_ACTIONS_VARIANTS.GALLERY]: {
      discard: HIT_ORIGINS.AD_IMAGE_GALLERY_AD_DISCARDED,
      favorite: actionAdFavoriteTrackingOrigins.IMAGE_GALLERY_AD_FAVORITED,
      unfavorite: actionAdUnfavoriteTrackingOrigins.IMAGE_GALLERY_AD_UNFAVORITED,
      share: actionAdShareTrackingOrigins.IMAGE_GALLERY_AD_SHARED
    }
  }
  const origins = ORIGINS_BY_VARIANT[variant]

  const referrer = ad.url ? `${companyUrl}${ad.url}` : companyUrl

  const handleDiscardAd = () => {
    const backUrl = hrefBack || getDefaultBackUrl({breadcrumb, adsListPathPattern})
    // router.push expects a path; the breadcrumb urls can come back absolute from the backend
    const {pathname, search} = new URL(backUrl, window.location.origin)
    router.push({pathname, search, state: {discardAd: ad.id}})
  }

  const handleShareAction = ({url}) => {
    window.open(url, '_blank')
  }

  return (
    <TooltipProvider>
      <div className={`${BASE_CLASS}-actions`}>
        {hasDiscardButton && (
          <ActionTooltip label={i18n.t('CARD_AD_DISCARD_AD')}>
            <ActionsAdDiscard
              adId={ad.id}
              isFavorite={isFavorite}
              onAfterDiscard={handleDiscardAd}
              origin={origins.discard}
              size={atomButtonSizes.LARGE}
              type={isNegative ? actionsAdDiscardTypes.ICON_BUTTON_NEGATIVE : actionsAdDiscardTypes.ICON_BUTTON}
            />
          </ActionTooltip>
        )}
        <ActionTooltip label={i18n.t('COMMON.AD_SHARE')}>
          <ActionsAdShare
            ad={ad}
            contractId={contractId}
            extendedTrackingData={extendedTrackingData}
            icon={<IconShareOutline />}
            onAction={handleShareAction}
            referer={referrer}
            size={actionAdShareButtonSizes.LARGE}
            trackingOrigin={origins.share}
            type={isNegative ? actionAdShareTypes.ICON_BUTTON_NEGATIVE : actionAdShareTypes.ICON_BUTTON}
          />
        </ActionTooltip>
        <ActionTooltip label={i18n.t(isFavorite ? 'CARD_AD_DELETE_FAVORITE' : 'FAVORITES.ADD')}>
          <FavoriteLeadPopover
            isOpen={isFavoriteLeadEnabled && isFavoriteLeadPopoverOpen}
            onClose={handleFavoriteLeadPopoverClose}
            onConfirm={onFavoriteLeadConfirm}
          >
            <ActionAdFavorite
              ad={ad}
              extendedTrackingData={extendedTrackingData}
              isFavorite={isFavorite}
              isOnClickCallbackReady={isFavoriteLeadEnabled}
              onClick={handleFavoriteAction}
              size={atomButtonSizes.LARGE}
              trackingOrigin={origins.favorite}
              unfavoriteTrackingOrigin={origins.unfavorite}
              type={isNegative ? actionAdFavoriteTypes.ICON_BUTTON_NEGATIVE : actionAdFavoriteTypes.ICON_BUTTON}
            />
          </FavoriteLeadPopover>
        </ActionTooltip>
      </div>
    </TooltipProvider>
  )
}

AdActions.displayName = 'AdActions'
AdActions.propTypes = {
  ad: PropTypes.object.isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({url: PropTypes.string})),
  contractId: PropTypes.string,
  extendedTrackingData: PropTypes.object,
  hasDiscardButton: PropTypes.bool,
  hrefBack: PropTypes.string,
  isFavorite: PropTypes.bool,
  isFavoriteLeadEnabled: PropTypes.bool,
  isNegative: PropTypes.bool,
  onFavoriteLeadConfirm: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(AD_ACTIONS_VARIANTS))
}

export default AdActions
